import React from "react";
import Navigation from "../components/site/Navigation";
import Footer from "../components/site/Footer";
import Reveal from "../components/site/Reveal";
import PageSeo from "../components/site/PageSeo";
import OrientationResetCheckoutHost from "../components/site/OrientationResetCheckoutHost";
import { ExternalLink } from "lucide-react";
import { SITE_URL, ORGANIZATION, PERSON } from "../data/entityGraph";

/**
 * /press
 *
 * Media + validation hub. Append entries to APPEARANCES when new
 * press lands. Each item produces both a visual card and a
 * CreativeWork JSON-LD node so Google can attribute the mention
 * to the founder.
 */
const APPEARANCES = [
  {
    id: "smile-channel",
    outlet: "The Smile Channel",
    title: "On signal over strategy, and the work of moving from force into flow.",
    kind: "Conversation",
    year: "2026",
    href: "https://www.thesmilechannel.com",
    summary:
      "Cyrus Prescod sits down with The Smile Channel to walk through the origin of the Authentic Momentum framework and the difference between forcing a path and being led by signal.",
  },
];

const PressPage = () => {
  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${SITE_URL}/press#collection`,
    url: `${SITE_URL}/press`,
    name: "Press and Validation - Authentic Momentum",
    about: { ...PERSON },
    hasPart: APPEARANCES.map((a) => ({
      "@type": "CreativeWork",
      name: a.title,
      about: { ...PERSON },
      isPartOf: { "@type": "MediaObject", name: a.outlet, url: a.href },
      url: a.href,
      datePublished: a.year,
    })),
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Press", item: `${SITE_URL}/press` },
    ],
  };

  return (
    <>
      <PageSeo
        title="Press and Validation | Authentic Momentum"
        description="Featured conversations, media appearances, and external recognition of Cyrus Prescod and the Authentic Momentum framework."
        canonical={`${SITE_URL}/press`}
        jsonLd={[collectionLd, breadcrumbLd, { "@context": "https://schema.org", ...ORGANIZATION }]}
      />
      <Navigation />
      <main className="relative pt-32 pb-32 noise-overlay min-h-screen">
        <div className="absolute inset-0 gold-glow-radial pointer-events-none" aria-hidden />

        <section className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
              Press and Validation
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-themed leading-tight tracking-tight font-light">
              Where the work has{" "}
              <span className="italic gold-gradient-text">surfaced.</span>
            </h1>
            <p className="mt-7 text-white/85 leading-relaxed max-w-2xl mx-auto text-[17px]">
              Conversations, features, and signal-based mentions of the Authentic Momentum framework and the people building it.
            </p>
          </Reveal>
        </section>

        <section
          data-testid="press-appearances"
          className="relative max-w-5xl mx-auto px-6 md:px-10 mt-20 space-y-5"
        >
          {APPEARANCES.map((a, i) => (
            <Reveal key={a.id} delay={120 + i * 90}>
              <a
                href={a.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`press-card-${a.id}`}
                className="block bg-white/5 border-t border-gold/20 p-7 md:p-9 rounded-lg border border-gold/10 hover:border-gold/30 transition-colors group"
              >
                <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-2 md:gap-6">
                  <div>
                    <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85">
                      {a.outlet}
                    </p>
                    <h2 className="font-serif text-xl md:text-2xl text-themed font-light leading-snug mt-2 group-hover:text-gold transition-colors">
                      {a.title}
                    </h2>
                  </div>
                  <p className="text-[11px] tracking-[0.3em] uppercase text-gold/85 md:text-right shrink-0">
                    {a.kind} · {a.year}
                  </p>
                </div>
                <p className="mt-5 max-w-3xl text-white/85 text-[15.5px] leading-relaxed">
                  {a.summary}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-gold">
                  Visit the source <ExternalLink size={12} />
                </span>
              </a>
            </Reveal>
          ))}
        </section>

        <section className="relative max-w-3xl mx-auto px-6 md:px-10 mt-24 text-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-4">
              For Press and Conversations
            </p>
            <p className="text-white/85 leading-relaxed max-w-xl mx-auto">
              Writers, podcasters, and producers can reach the team directly at{" "}
              <a
                href="mailto:hello@authentic-momentum.com?subject=Press+Inquiry"
                data-testid="press-email"
                className="text-gold hover:underline underline-offset-4"
              >
                hello@authentic-momentum.com
              </a>
              . We respond to thoughtful inquiries within a few days.
            </p>
          </Reveal>
        </section>
      </main>
      <Footer />
      <OrientationResetCheckoutHost />
    </>
  );
};

export default PressPage;
